import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import TuneIcon from "@mui/icons-material/Tune";
import { isMobile } from "../../globalStyles/mobile";
import { forecastDataService } from "../../utils/services/forecast-data.service";
import { formatDateTime } from "../../utils/formatDate";
import {
  useMetricsUi,
  PAGE_FONT,
  MONO_FONT,
} from "../../components/metrics/metricsUi";
import ForecastParamsPanel from "./ForecastParamsPanel";
// Unchanged between this page and the classic ../planning-forecast — shared
// from there rather than duplicated.
import ForecastResults from "../planning-forecast/ForecastResults";
import { ForecastOverview, forecastHeadline } from "../planning-forecast/ForecastResultView";
import ForecastRunningSteps from "../planning-forecast/ForecastRunningSteps";
import { latestBatchRuns } from "../planning-forecast/latestBatchRuns";
import { ForecastResultSkeleton } from "../planning-forecast/forecastUi";
import ForecastPlannerAgent from "./ForecastPlannerAgent";
import ForecastArtifactButton from "./ForecastArtifactButton";
import AutocompleteWithPagination from "../../components/autocomplete-with-pagination";
import { clientService } from "../../utils/services/clientService";

const CUSTOM_PANEL_DEFAULT_WIDTH = 480;
const CUSTOM_PANEL_MIN_WIDTH = 340;
const CUSTOM_PANEL_MAX_WIDTH = 900;

const OPTION_PAGE = 20;

const optionLabel = (by, o) => {
  if (!o) return "";
  if (by === "Product") {
    const n = o.sku_count || 0;
    return `${o.label}  ·  ${n} SKU${n === 1 ? "" : "s"}`;
  }
  return o.latest_at
    ? `${o.label}  ·  ${formatDateTime(o.latest_at)}`
    : o.label;
};

const skuOf = (run) => run?.sku || run?.input?.sku || "";

const UPDATED_CLIENT_KEY = "updated_forecast_client";
const readSavedClient = () => {
  try {
    return JSON.parse(localStorage.getItem(UPDATED_CLIENT_KEY) || "null");
  } catch (e) {
    return null;
  }
};
const isAdminUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null")?.role === "admin";
  } catch (e) {
    return false;
  }
};

// "Planning > Forecast New" — the full-featured version, with the "Custom"
// AI chat panel (draftForecast/runForecast) on top of everything the classic
// ../planning-forecast page already had. The classic page was reverted back
// to its pre-agent state; this is where active development continues.
// `variant="updated"` runs forecasts on the standalone demand-forecast service
// (engine "demand_forecast"). Admins reach it as Inventory → Updated Forecast,
// pick the client first, and get no forecast helper panel; client users get it
// as their Forecast page, for their own account, with the helper ("Custom")
// and Sales Fill read-only. The Product Artifact stays, as its own
// report built from this engine's runs (separate from the planning pages').
const PlanningForecastNew = ({ variant = "planning" }) => {
  const isUpdated = variant === "updated";
  const engine = isUpdated ? "demand_forecast" : undefined;
  // The forecast helper agent ("Custom"): on the client's Forecast page, not on
  // the admin Updated Forecast. Set below once we know who's signed in.
  const ui = useMetricsUi();
  const { colors } = ui;
  const [selectedClient, setSelectedClient] = useState(() =>
    isUpdated ? readSavedClient() : null,
  );
  const isAdmin = isAdminUser();
  const enableAgent = !isUpdated || !isAdmin;
  // Updated Forecast: admins pick the client; a client user is their own client.
  const clientId = isUpdated && isAdmin
    ? selectedClient?._id || ""
    : localStorage.getItem("client_id") || "";
  const chooseClient = (client) => {
    setSelectedClient(client || null);
    try {
      if (client?._id) {
        localStorage.setItem(
          UPDATED_CLIENT_KEY,
          JSON.stringify({ _id: client._id, business_name: client.business_name, name: client.name }),
        );
      } else {
        localStorage.removeItem(UPDATED_CLIENT_KEY);
      }
    } catch (e) {
      // storage unavailable — the choice just isn't remembered
    }
  };

  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // "Custom" opens the forecast AI agent in a resizable side panel — inline,
  // not a modal drawer, so the rest of the page stays visible and usable to
  // its left.
  const [customOpen, setCustomOpen] = useState(false);
  const [panelWidth, setPanelWidth] = useState(CUSTOM_PANEL_DEFAULT_WIDTH);
  const resizingRef = useRef(false);
  // A plan the agent drafted — handed to ForecastParamsPanel to pre-fill the
  // form; the agent never submits, the human reviews and runs it themselves.
  const [agentDraft, setAgentDraft] = useState(null);

  // History browser — step 1: Product | SKU
  const [historyBy, setHistoryBy] = useState("Product");
  // step 2: the paginated list of products / SKUs seen in stored runs
  const [options, setOptions] = useState([]);
  const [optionsTotal, setOptionsTotal] = useState(0);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [historyKey, setHistoryKey] = useState("");

  // Actual forecast-data docs behind the current selection.
  const [historyRuns, setHistoryRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  // Every run produced by the most recently completed forecast (could be
  // several SKUs at once) — shown together, taking priority over the
  // History By selection, until the user picks something else. Without
  // this, a multi-SKU run would collapse down to whichever single SKU
  // happened to be first, since "browse history by SKU" is inherently
  // one-SKU-at-a-time (unlike "by Product", which already pulls in every
  // SKU under that product).
  const [freshRuns, setFreshRuns] = useState([]);

  const reqId = useRef(0);
  // In-flight request for each endpoint — aborted whenever a newer call
  // supersedes it, so a rapid sequence of dropdown/tab changes doesn't leave
  // several redundant forecast-data requests running concurrently.
  const optionsAbortRef = useRef(null);
  const runsAbortRef = useRef(null);

  // Abort anything still in flight when the page itself unmounts.
  useEffect(() => {
    return () => {
      optionsAbortRef.current?.abort();
      runsAbortRef.current?.abort();
    };
  }, []);

  // Fetch a page of history options for `by`; append or replace.
  const fetchOptions = useCallback(
    async (by, { append = false } = {}) => {
      optionsAbortRef.current?.abort();
      const controller = new AbortController();
      optionsAbortRef.current = controller;
      setOptionsLoading(true);
      try {
        const res = await forecastDataService.groups(clientId, {
          by: by.toLowerCase(),
          engine,
          limit: OPTION_PAGE,
          skip: append ? options.length : 0,
          signal: controller.signal,
        });
        const rows = Array.isArray(res?.data) ? res.data : [];
        setOptions((prev) => (append ? [...prev, ...rows] : rows));
        setOptionsTotal(res?.total || 0);
        return res;
      } catch (e) {
        console.error("Failed to load forecast history options", e);
        if (!append) {
          setOptions([]);
          setOptionsTotal(0);
        }
        return null;
      } finally {
        if (optionsAbortRef.current === controller) setOptionsLoading(false);
      }
    },
    [clientId, options.length, engine],
  );

  // Initial load — prefer Product history, fall back to SKU, else open the form.
  useEffect(() => {
    let cancelled = false;
    // A new client means a new history: start from a clean slate.
    setFreshRuns([]);
    setOptions([]);
    setOptionsTotal(0);
    setHistoryKey("");
    setHistoryRuns([]);
    setEditing(false);
    if (!clientId) {
      setLoading(false);
      return undefined;
    }
    (async () => {
      setLoading(true);
      setHistoryBy("Product");
      let res = await fetchOptions("Product", { append: false });
      if (cancelled) return;
      if (!(res?.total > 0)) {
        setHistoryBy("SKU");
        res = await fetchOptions("SKU", { append: false });
        if (cancelled) return;
      }
      if (!(res?.total > 0)) setEditing(true);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // Keep the step-2 selection valid: default to the first option.
  useEffect(() => {
    setHistoryKey((prev) =>
      prev && options.some((o) => o.value === prev)
        ? prev
        : options[0]?.value || "",
    );
  }, [options]);

  // Load the forecast-data docs for the current selection.
  useEffect(() => {
    runsAbortRef.current?.abort();
    if (!historyKey) {
      setHistoryRuns([]);
      return undefined;
    }
    const myReq = ++reqId.current;
    const controller = new AbortController();
    runsAbortRef.current = controller;
    setRunsLoading(true);
    (async () => {
      try {
        const res =
          historyBy === "Product"
            ? await forecastDataService.list(clientId, {
                product: historyKey,
                forecastBy: "product",
                engine,
                limit: 300,
                signal: controller.signal,
              })
            : await forecastDataService.list(clientId, {
                skuExact: historyKey,
                forecastBy: "sku",
                engine,
                limit: 1,
                signal: controller.signal,
              });
        if (myReq !== reqId.current) return;
        // The server already scopes to runs generated AT this level — an
        // individual SKU re-run under a product (or vice versa) is a
        // different, ad-hoc thing (different horizon/params) and shouldn't
        // mix into this history.
        const list = Array.isArray(res?.data) ? res.data : [];
        if (historyBy === "Product") {
          // One tab per SKU: its record from the product's latest forecast
          // batch, or its own newest record if it wasn't in that batch —
          // the same records the Product Artifact is built from.
          setHistoryRuns(latestBatchRuns(list, (r) => skuOf(r) || r._id));
        } else {
          // SKU mode — just the latest run for this SKU.
          setHistoryRuns(list.slice(0, 1));
        }
      } catch (e) {
        if (myReq === reqId.current) {
          console.error("Failed to load forecast runs", e);
          setHistoryRuns([]);
        }
      } finally {
        if (myReq === reqId.current) setRunsLoading(false);
      }
    })();
    return () => controller.abort();
  }, [clientId, historyBy, historyKey, engine]);

  const selected = useMemo(
    () => (freshRuns.length ? freshRuns[0] : historyRuns[0]) || null,
    [freshRuns, historyRuns],
  );
  const displayRuns = freshRuns.length ? freshRuns : historyRuns;

  // Which run's tab is actually on screen inside <ForecastResults> — kept in
  // sync via its onActiveRunChange so the Forecast Parameters panel reflects
  // whichever SKU/product the user picked, not always the first run. Cleared
  // whenever the run set itself changes, so a stale selection from the
  // previous SKU/product never flashes before ForecastResults reports back
  // its own (reset) tab.
  const [activeRun, setActiveRun] = useState(null);
  // Whether "All SKUs" (the whole product), not one specific SKU, is the
  // active selection — passed down so the Forecast Parameters panel can
  // hide the SKU field instead of showing one arbitrary SKU's code.
  const [isAllSkusView, setIsAllSkusView] = useState(false);
  const handleActiveRunChange = useCallback((run, meta) => {
    setActiveRun(run);
    setIsAllSkusView(Boolean(meta?.isAllSkus));
  }, []);
  // Only `activeRun` is cleared here — <ForecastResults> always reports the
  // freshly-resolved run (and isAllSkus) right after this fires, since its
  // own effect for the new `displayRuns` runs AFTER this one (child effects
  // fire before parent effects). Clearing `isAllSkusView` here too would
  // clobber that correct report back to `false` every time, which is
  // invisible for `activeRun` (ForecastParamsPanel falls back to `selected`)
  // but not for `isAllSkusView`, which has no such fallback.
  useEffect(() => {
    setActiveRun(null);
  }, [displayRuns]);

  // The product the Artifact button reports on — whichever run is on screen.
  const artifactRun = activeRun || selected;
  const artifactProductId = String(
    artifactRun?.input?.product?._id || artifactRun?.input?.product || "",
  );

  const changeHistoryBy = (value) => {
    setFreshRuns([]);
    setHistoryBy(value);
    setHistoryKey("");
    setHistoryRuns([]);
    setOptions([]);
    setOptionsTotal(0);
    fetchOptions(value, { append: false });
  };

  const onForecasted = async (ids) => {
    setEditing(false);
    const list = Array.isArray(ids) ? ids : ids ? [ids] : [];
    if (!list.length) return;

    // Fetch every run this forecast just produced — not only the first —
    // so a multi-SKU (or category/product-type-expanded) batch displays all
    // of them together, the same completeness Product mode already gets.
    let docs = [];
    try {
      docs = (
        await Promise.all(
          list.map((id) =>
            forecastDataService
              .get(id)
              .then((r) => r?.data)
              .catch(() => null),
          ),
        )
      ).filter(Boolean);
    } catch (e) {
      console.error("Failed to load the new forecast run(s)", e);
    }
    setFreshRuns(docs);

    const doc = docs[0] || null;
    const by =
      String(doc?.input?.forecast_by || "").toLowerCase() === "product"
        ? "Product"
        : "SKU";
    const key =
      by === "Product"
        ? String(doc?.input?.product?._id || doc?.input?.product || "")
        : String(doc?.sku || "");
    setHistoryBy(by);
    setOptions([]);
    setOptionsTotal(0);
    await fetchOptions(by, { append: false });
    if (key) setHistoryKey(key);
  };

  // The agent's draftForecast tool calls this — open the form in edit mode
  // pre-filled with its plan; nothing is submitted until the user reviews it
  // and clicks Run Forecast themselves.
  const onAgentDraft = (draft) => {
    console.log(draft);

    setAgentDraft(draft);
    setFreshRuns([]);
    setEditing(true);
  };

  // Imperative handle onto ForecastParamsPanel — lets the agent's
  // `runForecast` uiTool trigger the exact same submit path as clicking
  // "Run Forecast" on whatever's currently in the form. Separate from
  // draftForecast/onAgentDraft above; doesn't touch that flow at all.
  const formRef = useRef(null);
  const onAgentRunForecast = async () => {
    if (!editing || !formRef.current) {
      return {
        success: false,
        message:
          "There's no forecast plan open to run right now — draft one first.",
      };
    }
    return formRef.current.runForecast();
  };

  // Drag-to-resize the Custom panel from its left edge.
  const startPanelResize = useCallback((e) => {
    e.preventDefault();
    resizingRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!resizingRef.current) return;
      const next = window.innerWidth - e.clientX;
      const max = Math.min(CUSTOM_PANEL_MAX_WIDTH, window.innerWidth - 240);
      setPanelWidth(Math.min(Math.max(next, CUSTOM_PANEL_MIN_WIDTH), max));
    };
    const onUp = () => {
      if (!resizingRef.current) return;
      resizingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const canLoadMore = options.length < optionsTotal;
  const hasHistory = options.length > 0;

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flex: "1 1 auto",
          minWidth: 0,
          height: "100%",
          overflowY: "auto",
          backgroundColor: ui.darkMode ? colors.bgPrimary : "#F5F6F8",
          fontFamily: PAGE_FONT,
        }}
      >
        <Box
          sx={{
            width: "100%",
            padding: isMobile() ? "14px 12px 40px" : "22px 28px 56px",
          }}
        >
          {/* ---------- Topbar (sticky — stays put while results scroll) ----------
            This left column is its own scroll container (the panel next to it
            scrolls independently). Bleed the bar out over this column's own
            padding on every side, then add it back as inner padding, so the
            bar spans edge-to-edge and its solid background fully hides
            content scrolling underneath. */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              backgroundColor: ui.darkMode ? colors.bgPrimary : "#F5F6F8",
              borderBottom: `1px solid ${colors.border}`,
              mx: isMobile() ? "-12px" : "-28px",
              px: isMobile() ? "12px" : "28px",
              mt: isMobile() ? "-14px" : "-22px",
              pt: isMobile() ? "14px" : "22px",
              pb: 1.5,
              mb: 2,
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "9px",
                  background: `linear-gradient(155deg, ${ui.accent}, #1B3FA8)`,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: 13,
                  fontFamily: MONO_FONT,
                  flexShrink: 0,
                }}
              >
                FC
              </Box>
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: colors.textPrimary,
                  lineHeight: 1,
                }}
              >
                {isUpdated && isAdmin ? "Updated Forecast" : "Forecast"}
              </Typography>

              {isUpdated && isAdminUser() && (
                <Box sx={{ width: 260, maxWidth: "100%" }}>
                  <AutocompleteWithPagination
                    selectedItem={selectedClient}
                    onDataChange={(val) => chooseClient(val)}
                    width="100%"
                    optionKey={(c) => c?.business_name || c?.name || ""}
                    backgroundColor="var(--bg-paper)"
                    customLabel="Client"
                    apiEndPoint={(limit, skip, search) =>
                      clientService.fetch(limit, skip, search, null, "Active")
                    }
                    disabled={submitting}
                  />
                </Box>
              )}

              {hasHistory && !editing && (
                <>
                  {/* Step 1 — browse history by Product or by SKU */}
                  <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel id="pf-mode-label">History By</InputLabel>
                    <Select
                      labelId="pf-mode-label"
                      label="History By"
                      value={historyBy}
                      onChange={(e) => changeHistoryBy(e.target.value)}
                      sx={{
                        backgroundColor: colors.bgPaper,
                        fontSize: 13,
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: colors.border,
                        },
                      }}
                    >
                      <MenuItem value="Product" sx={{ fontSize: 13 }}>
                        Product
                      </MenuItem>
                      <MenuItem value="SKU" sx={{ fontSize: 13 }}>
                        SKU
                      </MenuItem>
                    </Select>
                  </FormControl>

                  {/* Step 2 — the products / SKUs seen in recent forecast runs */}
                  <FormControl
                    size="small"
                    sx={{ minWidth: 260, maxWidth: "100%" }}
                  >
                    <InputLabel id="pf-key-label">
                      {historyBy === "Product" ? "Product" : "SKU"}
                    </InputLabel>
                    <Select
                      labelId="pf-key-label"
                      label={historyBy === "Product" ? "Product" : "SKU"}
                      value={
                        options.some((o) => o.value === historyKey)
                          ? historyKey
                          : ""
                      }
                      onChange={(e) => {
                        setFreshRuns([]);
                        setHistoryKey(e.target.value);
                      }}
                      MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
                      sx={{
                        backgroundColor: colors.bgPaper,
                        fontSize: 13,
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: colors.border,
                        },
                      }}
                    >
                      {options.map((o) => (
                        <MenuItem
                          key={o.value}
                          value={o.value}
                          sx={{ fontSize: 13 }}
                        >
                          {optionLabel(historyBy, o)}
                        </MenuItem>
                      ))}
                      {canLoadMore && (
                        <ListSubheader
                          disableSticky
                          sx={{
                            p: 0,
                            bgcolor: "transparent",
                            lineHeight: "normal",
                          }}
                        >
                          <Button
                            fullWidth
                            size="small"
                            // keep the menu open — this row isn't a selectable option
                            onClick={() =>
                              fetchOptions(historyBy, { append: true })
                            }
                            disabled={optionsLoading}
                            sx={{
                              fontSize: 12,
                              textTransform: "none",
                              py: 0.75,
                              borderRadius: 0,
                            }}
                          >
                            {optionsLoading
                              ? "Loading…"
                              : `Load more (${optionsTotal - options.length})`}
                          </Button>
                        </ListSubheader>
                      )}
                    </Select>
                  </FormControl>
                </>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 1.25,
              }}
            >
              {enableAgent && (
                <Button
                  variant={customOpen ? "contained" : "outlined"}
                  color="primary"
                  startIcon={
                    customOpen ? <KeyboardDoubleArrowRightIcon /> : <TuneIcon />
                  }
                  onClick={() => {
                    if (customOpen) {
                      setCustomOpen(false);
                      return;
                    }
                    // Activate the form (same as "New") so it's ready behind
                    // the panel for the agent to fill in via draftForecast.
                    setFreshRuns([]);
                    setEditing(true);
                    setCustomOpen(true);
                  }}
                >
                  {customOpen ? "Close" : "Custom"}
                </Button>
              )}
              {!editing && (
                <ForecastArtifactButton
                  engine={engine}
                  clientId={isUpdated ? clientId : undefined}
                  productId={artifactProductId}
                  // One SKU on screen → that SKU's own report.
                  skuRun={!isAllSkusView ? artifactRun : null}
                  disabled={runsLoading}
                />
              )}
              {!editing && Boolean(clientId) && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AutorenewIcon />}
                  onClick={() => {
                    setFreshRuns([]);
                    setEditing(true);
                  }}
                >
                  New
                </Button>
              )}
            </Box>
          </Box>

          {isUpdated && !clientId ? (
            <Box sx={{ ...ui.card, textAlign: "center", py: 7, px: 3 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 16, color: colors.textPrimary, mb: 0.5 }}>
                Choose a client
              </Typography>
              <Typography sx={{ color: colors.textSecondary, fontSize: 13, maxWidth: 520, mx: "auto" }}>
                Pick a client at the top to see their updated forecasts, or run a new one for their SKUs,
                products or product types. Forecasts here use the new model-selection service and don't
                change the client's live forecasts.
              </Typography>
            </Box>
          ) : loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
              <CircularProgress />
            </Box>
          ) : !editing && !hasHistory ? (
            <Box
              sx={{
                ...ui.card,
                textAlign: "center",
                py: 7,
                px: 3,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 16,
                  color: colors.textPrimary,
                  mb: 0.5,
                }}
              >
                No forecasts yet
              </Typography>
              <Typography
                sx={{ color: colors.textSecondary, fontSize: 13, mb: 2 }}
              >
                Run a forecast for one or more SKUs to see its parameters and
                projected demand here.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AutorenewIcon />}
                onClick={() => setEditing(true)}
              >
                Run Forecast
              </Button>
            </Box>
          ) : !editing && !selected ? (
            runsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ ...ui.card, textAlign: "center", py: 7, px: 3 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 16,
                    color: colors.textPrimary,
                    mb: 0.5,
                  }}
                >
                  No forecast runs found
                </Typography>
                <Typography
                  sx={{ color: colors.textSecondary, fontSize: 13, mb: 2 }}
                >
                  Pick a different {historyBy === "Product" ? "product" : "SKU"}{" "}
                  from the history list, or run a new forecast.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AutorenewIcon />}
                  onClick={() => setEditing(true)}
                >
                  Run Forecast
                </Button>
              </Box>
            )
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Parameters (view / inline-edit) */}
              <ForecastParamsPanel
                key={isUpdated ? clientId : undefined}
                ref={formRef}
                engine={engine}
                salesFillLocked={isUpdated && !isAdmin}
                run={activeRun || selected}
                clientId={clientId}
                editing={editing}
                draft={agentDraft}
                onDraftConsumed={() => setAgentDraft(null)}
                onCancel={() => setEditing(false)}
                onForecasted={onForecasted}
                onSubmittingChange={setSubmitting}
                isAllSkusView={isAllSkusView}
                // Updated Forecast: Parameters, Summary and Method collapse
                // into this one panel so the Insight and chart are on screen.
                defaultCollapsed={isUpdated}
                extra={
                  isUpdated && !editing && !isAllSkusView && (activeRun || selected) ? (
                    <ForecastOverview run={activeRun || selected} />
                  ) : null
                }
                collapsedHint={
                  isUpdated && !isAllSkusView ? forecastHeadline(activeRun || selected) : ""
                }
              />

              {/* Result skeleton while a run is in flight */}
              {editing && submitting && <ForecastRunningSteps />}

              {/* Result — one or more forecast runs, shown together in tabs.
                  A just-completed batch (freshRuns) takes priority over the
                  History By selection until the user picks something else —
                  it already has everything the run produced, so there's no
                  history-fetch spinner to wait through for it. */}
              {!editing &&
                (freshRuns.length === 0 && runsLoading ? (
                  <ForecastResultSkeleton message="" />
                ) : (
                  displayRuns.length > 0 && (
                    <ForecastResults
                      runs={displayRuns}
                      onActiveRunChange={handleActiveRunChange}
                      autoGenerateArtifact
                      showArtifact
                      engine={engine || "kaman"}
                      hideOverview={isUpdated}
                    />
                  )
                ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* "Custom" — the forecast AI agent, in a resizable side panel docked
          next to the page (not a modal drawer), so the rest of the page
          stays visible and usable to its left. A fixed-height sibling, not
          part of the left column's scroll — it never moves, and only its own
          chat content scrolls internally, independently of the left column.
          Drag the left edge to resize. */}
      {enableAgent && customOpen && (
        <Box
          sx={{
            flexShrink: 0,
            width: panelWidth,
            display: "flex",
            height: "100%",
            overflow: "hidden",
            backgroundColor: ui.darkMode ? colors.bgPrimary : "#F5F6F8",
            borderLeft: `1px solid ${colors.border}`,
          }}
        >
          <Box
            onMouseDown={startPanelResize}
            title="Drag to resize"
            sx={{
              width: "6px",
              flexShrink: 0,
              cursor: "col-resize",
              "&:hover": { backgroundColor: colors.border },
            }}
          />
          <Box sx={{ flex: "1 1 auto", minWidth: 0, height: "100%", pt: 1.5 }}>
            <ForecastPlannerAgent
              chatHeight="100%"
              onDraftForecast={onAgentDraft}
              onRunForecast={onAgentRunForecast}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PlanningForecastNew;
